import { z } from 'zod'

import { sha256CanonicalJson } from './serialization.ts'

const identifier = z
  .string()
  .min(1)
  .max(120)
  .regex(
    /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/,
    'Use lowercase letters, numbers, dots, dashes, or underscores'
  )

const boundedName = z.string().trim().min(1).max(200)
const emojiPresentation = /\p{Extended_Pictographic}/u
const flagEmoji = /^\p{Regional_Indicator}{2}$/u
const keycapEmoji = /^[0-9#*]\uFE0F?\u20E3$/u
const scenarioEmoji = z
  .string()
  .trim()
  .min(1)
  .max(32)
  .refine((value) => {
    const segments =
      typeof Intl.Segmenter === 'function'
        ? [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(value)]
        : Array.from(value)
    return (
      segments.length === 1 &&
      (emojiPresentation.test(value) || flagEmoji.test(value) || keycapEmoji.test(value))
    )
  }, 'Emoji must be exactly one emoji grapheme')
const relativeRoute = z
  .string()
  .min(1)
  .max(2_048)
  .refine((value) => value.startsWith('/') && !value.startsWith('//') && !value.includes('\\'), {
    message: 'Routes must start with one slash and cannot be absolute URLs'
  })

export const WebTestTargetKind = z.enum(['web-lab', 'electron'])
export type WebTestTargetKind = z.infer<typeof WebTestTargetKind>

export const LocatorHint = z.discriminatedUnion('by', [
  z
    .object({
      by: z.literal('role'),
      role: boundedName,
      name: boundedName.optional(),
      exact: z.boolean().optional()
    })
    .strict(),
  z.object({ by: z.literal('label'), value: boundedName, exact: z.boolean().optional() }).strict(),
  z
    .object({ by: z.literal('placeholder'), value: boundedName, exact: z.boolean().optional() })
    .strict(),
  z.object({ by: z.literal('text'), value: boundedName, exact: z.boolean().optional() }).strict(),
  z.object({ by: z.literal('test-id'), value: boundedName }).strict(),
  z
    .object({
      by: z.literal('css'),
      value: z
        .string()
        .trim()
        .min(1)
        .max(500)
        .refine((value) => !/(?:xpath\s*=|\/\/)|^\.\./i.test(value), {
          message: 'XPath locators are not allowed; provide an approved CSS selector'
        }),
      rationale: z.string().trim().min(1).max(500)
    })
    .strict()
])
export type LocatorHint = z.infer<typeof LocatorHint>

const modifierKeys = z
  .array(z.enum(['Alt', 'Control', 'Meta', 'Shift']))
  .max(4)
  .optional()

export const ScenarioAction = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('navigate'), route: relativeRoute }).strict(),
  z
    .object({
      kind: z.literal('click'),
      target: LocatorHint,
      button: z.enum(['left', 'right', 'middle']).optional()
    })
    .strict(),
  z
    .object({
      kind: z.literal('fill'),
      target: LocatorHint,
      value: z.string().max(20_000),
      sensitive: z.boolean().optional()
    })
    .strict(),
  z
    .object({
      kind: z.literal('select'),
      target: LocatorHint,
      value: z.union([z.string(), z.array(z.string()).min(1)]),
      sensitive: z.boolean().optional()
    })
    .strict(),
  z.object({ kind: z.literal('check'), target: LocatorHint }).strict(),
  z.object({ kind: z.literal('uncheck'), target: LocatorHint }).strict(),
  z
    .object({
      kind: z.literal('press'),
      target: LocatorHint.optional(),
      key: boundedName,
      modifiers: modifierKeys
    })
    .strict(),
  z.object({ kind: z.literal('hover'), target: LocatorHint }).strict(),
  z.object({ kind: z.literal('drag'), source: LocatorHint, target: LocatorHint }).strict(),
  z
    .object({
      kind: z.literal('wait'),
      for: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('visible'), target: LocatorHint }).strict(),
        z.object({ kind: z.literal('hidden'), target: LocatorHint }).strict(),
        z.object({ kind: z.literal('route'), route: relativeRoute }).strict(),
        z.object({ kind: z.literal('text'), target: LocatorHint, value: boundedName }).strict(),
        z.object({ kind: z.literal('network-idle') }).strict()
      ])
    })
    .strict(),
  z
    .object({
      kind: z.literal('upload'),
      target: LocatorHint,
      fixture: identifier
    })
    .strict()
])
export type ScenarioAction = z.infer<typeof ScenarioAction>

const TextExpectation = z
  .object({
    equals: z.string().max(20_000).optional(),
    contains: z.string().max(20_000).optional(),
    matches: z.string().max(1_000).optional()
  })
  .strict()
  .refine((value) => Object.values(value).filter((entry) => entry !== undefined).length === 1, {
    message: 'Specify exactly one of equals, contains, or matches'
  })

export const ScenarioAssertion = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('visible'), target: LocatorHint }).strict(),
  z.object({ kind: z.literal('hidden'), target: LocatorHint }).strict(),
  z
    .object({ kind: z.literal('enabled'), target: LocatorHint, value: z.boolean().default(true) })
    .strict(),
  z
    .object({ kind: z.literal('checked'), target: LocatorHint, value: z.boolean().default(true) })
    .strict(),
  z.object({ kind: z.literal('text'), target: LocatorHint, expect: TextExpectation }).strict(),
  z.object({ kind: z.literal('value'), target: LocatorHint, expect: TextExpectation }).strict(),
  z
    .object({
      kind: z.literal('count'),
      target: LocatorHint,
      value: z.number().int().min(0).max(10_000)
    })
    .strict(),
  z.object({ kind: z.literal('route'), route: relativeRoute }).strict(),
  z.object({ kind: z.literal('title'), expect: TextExpectation }).strict(),
  z
    .object({
      kind: z.literal('accessibility'),
      target: LocatorHint,
      name: boundedName.optional(),
      description: z.string().trim().min(1).max(1_000).optional()
    })
    .strict(),
  z
    .object({
      kind: z.literal('screenshot'),
      name: identifier,
      target: LocatorHint.optional(),
      maxDifferenceRatio: z.number().min(0).max(1).optional()
    })
    .strict(),
  z
    .object({
      kind: z.literal('console'),
      allow: z.array(z.string().min(1).max(1_000)).max(50).default([])
    })
    .strict(),
  z
    .object({
      kind: z.literal('network'),
      allow: z.array(z.string().min(1).max(1_000)).max(50).default([])
    })
    .strict()
])
export type ScenarioAssertion = z.infer<typeof ScenarioAssertion>

export const ScenarioStep = z
  .object({
    id: identifier,
    description: z.string().trim().min(1).max(500),
    action: ScenarioAction.optional(),
    assertions: z.array(ScenarioAssertion).max(50).default([]),
    timeoutMs: z.number().int().min(100).max(120_000).optional(),
    explorationHint: z.string().trim().min(1).max(1_000).optional()
  })
  .strict()
  .refine((step) => step.action !== undefined || step.assertions.length > 0, {
    message: 'A step must contain an action, at least one assertion, or both'
  })
export type ScenarioStep = z.infer<typeof ScenarioStep>

export const ScenarioLifecycleStep = z
  .object({
    id: identifier,
    description: z.string().trim().min(1).max(500),
    action: ScenarioAction,
    timeoutMs: z.number().int().min(100).max(120_000).optional()
  })
  .strict()
export type ScenarioLifecycleStep = z.infer<typeof ScenarioLifecycleStep>

export const WebTestScenario = z
  .object({
    schemaVersion: z.literal(1),
    id: identifier,
    title: z.string().trim().min(1).max(200),
    emoji: scenarioEmoji.optional(),
    intent: z.string().trim().min(1).max(2_000),
    owner: z.string().trim().min(1).max(200).optional(),
    source: z.string().trim().min(1).max(500).optional(),
    tags: z.array(identifier).max(50).default([]),
    suites: z.array(identifier).min(1).max(20).default(['default']),
    targets: z.array(WebTestTargetKind).min(1).max(2).default(['web-lab']),
    start: z.object({ route: relativeRoute }).strict(),
    seed: z.object({ fixture: identifier }).strict().optional(),
    setup: z.array(ScenarioLifecycleStep).max(50).optional(),
    cleanup: z.array(ScenarioLifecycleStep).max(50).optional(),
    environment: z
      .object({
        viewport: z
          .object({
            width: z.number().int().min(320).max(3_840),
            height: z.number().int().min(480).max(2_160)
          })
          .strict()
          .default({ width: 1440, height: 900 }),
        colorScheme: z.enum(['light', 'dark', 'no-preference']).default('light'),
        locale: z.string().min(2).max(40).default('en-US'),
        timezone: z.string().min(1).max(100).default('UTC'),
        reducedMotion: z.enum(['reduce', 'no-preference']).default('reduce')
      })
      .strict()
      .default({
        viewport: { width: 1440, height: 900 },
        colorScheme: 'light',
        locale: 'en-US',
        timezone: 'UTC',
        reducedMotion: 'reduce'
      }),
    timeoutMs: z.number().int().min(1_000).max(900_000).default(300_000),
    cleanRetries: z.number().int().min(0).max(2).default(1),
    artifactPolicy: z
      .object({
        screenshots: z.enum(['failure', 'steps', 'all']).default('steps'),
        trace: z.enum(['failure', 'all']).default('failure'),
        recording: z.enum(['off', 'failure', 'all']).default('failure')
      })
      .strict()
      .default({ screenshots: 'steps', trace: 'failure', recording: 'failure' }),
    steps: z.array(ScenarioStep).min(1).max(200)
  })
  .strict()
  .superRefine((scenario, context) => {
    const stepIds = new Set<string>()
    const phases = [
      ['setup', scenario.setup ?? []],
      ['steps', scenario.steps],
      ['cleanup', scenario.cleanup ?? []]
    ] as const
    for (const [phase, steps] of phases) {
      for (const [index, step] of steps.entries()) {
        if (stepIds.has(step.id)) {
          context.addIssue({
            code: 'custom',
            message: `Duplicate step id "${step.id}"`,
            path: [phase, index, 'id']
          })
        }
        stepIds.add(step.id)
      }
    }
  })
export type WebTestScenario = z.infer<typeof WebTestScenario>

/**
 * Hash only the authored execution contract carried by deterministic plans.
 * `emoji` is durable dashboard presentation metadata, so changing it must not
 * invalidate a reviewed plan. The full scenario hash remains available to
 * callers that need stale-write protection or authored identity tracking.
 */
export async function executableScenarioHash(scenario: WebTestScenario): Promise<string> {
  const { emoji: _emoji, ...executableScenario } = scenario
  return sha256CanonicalJson(executableScenario)
}

export type ExecutionLocation = 'local' | 'remote'

export function assertScenarioTargetAllowed(
  scenario: WebTestScenario,
  target: WebTestTargetKind,
  location: ExecutionLocation
): void {
  if (!scenario.targets.includes(target)) {
    throw new Error(`Scenario "${scenario.id}" does not declare the "${target}" target`)
  }
  if (location === 'remote' && target === 'electron') {
    throw new Error('Electron scenarios are local-only and cannot run remotely or in CI')
  }
}
